import React from "react";
import { localizeWithMemberName } from "@/main/react/component/localizeWithMemberName";
import { createReactMount } from "@/main/react/mountContract";
import { MAIN_PAGE_LIST_EDGE_ON_PHONE_CLASS, MAIN_PAGE_LIST_WIDTH_CLASS, MainPageListStack } from "@/main/react/list/MainPageListLayout";
import {
    DiscussionCardListSkeleton,
    ProfileCardListSkeleton,
    SeriesCardListSkeleton,
} from "@/main/react/list/PaginatedListSkeletons";
import { PaginatedListWrapperV2 } from "@/main/react/list/PaginatedListWrapperV2";
import { createStandardPaginatedStatusSlots, PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { crawlableDiscussionListFallback } from "@/main/react/list/ssrFallbacks";
import { cn } from "@/lib/utils";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { onProfileUpdated, onUserFollowAdded, onUserFollowRemoved } from "@/main/common/EventRegistry";
import { useViewerMutationKey } from "@/main/react/list/viewerMutationKey";
import { Localizer } from "@/main/react/Types";
import { AchievementsRailLazy } from "@/main/react/user/AchievementsRailLazy";
import ProfileStatement, { PROFILE_SETTINGS_PATH } from "@/main/react/user/ProfileStatement";
import { ProfileShowcaseLazy } from "@/main/react/user/ProfileShowcaseLazy";
import { ProfileNewsletterCards } from "@/main/react/user/ProfileNewsletterCard";
import { ProfileVisitorInsights } from "@/main/react/user/ProfileVisitorInsights";
// A plain static import, NOT a lazy one: `UserPage` is itself a chunk (`preloadComponent`
// in user.jte), so this rides that chunk and never touches the eager startup graph rule 27
// budgets. It pulls in no heavy dependency of its own, which is the only reason the showcase
// beside it needed splitting and this does not.
import { ProfileWall } from "@/main/react/user/ProfileWall";
import { readMyToolsArrival } from "@/main/common/myToolsArrival";
import {
    ThoughtInSearchDTO,
    DiscussionWithoutContentDTO,
    GroupDTO,
    SeriesDTO,
    UserProfileDTO
} from "@/generated-client";
import {ThoughtCardDisplayer} from "@/main/react/card/ThoughtCardDisplayer";
import {DiscussionCardDisplayer} from "@/main/react/card/DiscussionCardDisplayer";
import {reportFeedPageShown} from "@/main/analytics/feedAttribution";
import {GroupCardDisplayer} from "@/main/react/card/GroupCardDisplayer";
import {SeriesCardDisplayer} from "@/main/react/card/SeriesCardDisplayer";
import {UserCardDisplayer} from "@/main/react/card/UserCardDisplayer";
import {useThumbnailLightbox} from "@/main/react/media/useThumbnailLightbox";
import {UserButtons} from "@/main/react/user/UserButtons";
import {UserHeader, UserHeaderControls, USER_HEADER_COUNT_TARGETS, type UserHeaderCountTarget, type UserHeaderProps} from "@/main/react/user/UserHeader";
import {PageWithRailLayout} from "@/main/react/page/PageWithRailLayout";
import {
    getUserArgumentOrderingOptions,
    getUserDiscussionOrderingOptions,
    getUserGroupOrderingOptions,
    getUserSeriesOrderingOptions,
    normalizeUserArgumentOrdering,
    normalizeUserDiscussionOrdering,
    normalizeUserGroupOrdering,
    normalizeUserSeriesOrdering,
    ProfilePageSelectOption,
    UserThoughtOrderingStyle,
    UserDiscussionOrderingStyle,
    UserGroupOrderingStyle,
    UserSeriesOrderingStyle,
} from "@/main/react/page/profile-page/ProfilePageOrdering";
import {
    fetchFollowersPage,
    fetchFollowingsPage,
    fetchUserArgumentsPage,
    fetchUserDiscussionsPage,
    fetchUserGroupsPage,
    fetchUserSeriesPage,
    UserDiscussionLanguageScope,
} from "@/main/react/page/profile-page/ProfilePageQueries";
import { DiscussionLanguageScopeSelect } from "@/main/react/page/profile-page/DiscussionLanguageScopeSelect";
import { DeletedContentEntryPointButton } from "@/main/react/page/profile-page/DeletedContentEntryPointButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Eye, FilterX, Image as ImageIcon, List, ListPlus, MessageCircle, MessageSquareQuote, MessageSquareReply, MessageSquareText, Search, ShieldCheck, Sparkles, UserPlus2, Users, UsersRound, X } from "lucide-react";
import SensitivePageGate from "@/main/react/sensitive/SensitivePageGate";
import type { SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import { useSensitivePageUnlock } from "@/main/react/sensitive/useSensitivePageUnlock";
import { getPageContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { useHistoryBackedState } from "@/main/react/page/useHistoryBackedState";
import { getDisplayUsername, normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { PageViewTracker } from "@/main/common/PageViewTracker";
import { filterBookmarksSeries } from "@/main/react/series/BookmarksSeriesLocalization";
import { applyClientRouteDocumentState } from "@/main/navigation/ClientRouteDocumentState";
import { resolveLandingSection } from "@/main/react/page/profile-page/ProfileLandingSection";
import { isPageInterceptableClick } from "@/main/navigation/NavigationPolicy";
import { logApiFailure } from "@/main/common/ApiUtils";
import { navigateToUrl } from "@/main/common/BrowserNavigation";

type Props = {
    initialSection?: string;
    username?: string;
    initiallyFollowing?: boolean;
    initialUserProfilePictureUrl?: string | null;
    initialUserDescription?: string | null;
    /**
     * The owner statement, server-rendered into the page dataset (slice F3, 2026-08-11). The island
     * seeds from it rather than waiting for a fetch, so first paint and mount agree about a block that
     * `user.jte` has already drawn.
     */
    initialUserProfileStatement?: string | null;
    initialUserCreationDate?: string | null;
    initialDiscussionsCount?: number | null;
    initialArgumentsCount?: number | null;
    initialAgreementsReceivedCount?: number | null;
    initialMindsChangedReceivedCount?: number | null;
    initialFollowersCount?: number | null;
    initialFollowingCount?: number | null;
    sensitive?: boolean;
    hasInitialBootstrap?: boolean;
    /**
     * D16's viewer-relative `canMessage`, server-rendered into `data-can-message` from the same
     * `UserProfileDTO.getCanMessage()` the bootstrap reports (round 26, `R25-canMessage`). `null` when the
     * server did not answer. It seeds the first render only; the bootstrap's boolean still wins (rule 32).
     */
    initialCanMessage?: boolean | null;
    /**
     * P9's viewer-dependent default, decided by `UserController` and written into `data-default-view`.
     *
     * The SERVER decides, for the same reason it already decides the landing section: it server-renders
     * the crawlable discussion list off its own answer, so an island that picked a different default
     * would replace the server's render one frame later and a crawler on a cold cache would index the
     * discarded one.
     */
    defaultMainColumnView?: string | null;
    localizer: Localizer;
};

/**
 * The six per-section sizes `UserPageBootstrapDTO.collectionCounts` publishes (batch 1,
 * `w16-collections-backend-design.md` §3; decision **D1**).
 *
 * Hand-written rather than imported, exactly like the sibling casts this file already carries for the
 * six account-wide counts: the generated client has not been regenerated for these fields and cannot
 * be without a running local backend. Every field is optional and nullable, and the three states are
 * meaningfully different — a number, `null` for "not yours to see", absent for "an older backend".
 */
type UserCollectionCounts = {
    participatedCount?: number | null;
    seriesCount?: number | null;
    groupsLeaderCount?: number | null;
    groupsMemberCount?: number | null;
    savedCount?: number | null;
    archivedCount?: number | null;
};

// `images` (round 12 `2e`, D30): the member's posts that carry media, `/user/{name}/images`. Mirrored in
// `UserController.VALID_USER_PAGE_SECTIONS` (pinned by `validUserPageSectionsMirrorTheFrontend`), and
// deliberately NOT a landing candidate (A18, `ProfileLandingSection.ts`).
// SV-147 A8 retired `replies` (round 31 `3a`): replies to short posts are thoughts, listed under Thoughts.
const USER_PAGE_SECTIONS = ["discussions", "images", "participated", "arguments", "followers", "following", "groups", "series"] as const;
const USER_GROUP_SECTIONS = ["leader", "member"] as const;

type UserPageSection = (typeof USER_PAGE_SECTIONS)[number];
type UserGroupSection = (typeof USER_GROUP_SECTIONS)[number];
/**
 * `wall` is the profile's default main-column view for a LOGGED-IN member and `sections` is the default
 * for everyone else — P9's viewer-dependent default, decided on the SERVER and handed over as
 * `data-default-view`.
 *
 * <p>The contradiction it resolves: P2 made the wall the profile's default view, while
 * `UserController.renderUserPage` server-renders the discussions list as crawlable links and the page
 * carries no `noindex`. Crawlers render, so a wall that replaced that list would replace a live SEO
 * internal-link hub *in the index*, not just on screen. An anonymous viewer therefore gets the hub and a
 * member gets the wall, which makes the indexed render and the anonymous render the same render — the
 * property that actually matters to rule 32.
 *
 * <p>The wall IS the eighth entry in the section picker (§6.1, reversed 2026-09-05), but
 * `USER_PAGE_SECTIONS` still stays at seven, and the two facts are not in tension. That tuple is the
 * vocabulary of the profile's PATH SEGMENT, not of the picker: `buildUserPageUrl` renders
 * `/user/{name}/{section}` out of it and `normalizeUserPageSection` accepts anything in it as a valid
 * path. Putting `wall` there would mint `/user/{name}/wall` — a new indexable URL of member-written
 * praise, which §6.1's constraint 2 refuses. The wall is a picker VALUE only, and its address stays
 * `?view=wall`.
 */
type UserMainColumnView = "wall" | "sections";

type UserRouteState = {
    /** Which of the two things the main column is showing. See {@link UserMainColumnView}. */
    view: UserMainColumnView;
    /**
     * The wall entry the address points at, as `KIND-sourceId`, or `""` for none — P8's per-entry
     * anchor, carried as ROUTE STATE rather than as a fragment.
     *
     * A bare `#fragment` would be invisible to `useHistoryBackedState`, which rebuilds its whole state
     * by re-reading the location on `popstate`; the anchor would then survive a reload and vanish on
     * Back. It is also what §8.4 needs so a notification opens the profile with ITS OWN entry in view
     * rather than merely opening the wall.
     *
     * ⚠️ Like every other field here it MUST be compared in `areEqual`. A field left out of that
     * comparison makes the change read as a no-op, the write is skipped, and the deep link silently
     * never opens — the group-announcement trap.
     */
    entry: string;
    section: UserPageSection;
    groupSection: UserGroupSection;
    /**
     * The current section's sort, in the URL rather than in component state.
     *
     * ONE field for all five sortable sections, not one per section — `useHistoryBackedState` rebuilds
     * its whole state by re-reading the location on `popstate`, so a sort kept in `useState` beside it
     * is simply gone by the time Back lands. That is what the upvotes count chip needs undone in one
     * press: it writes the section and the sort together, so both have to ride the same history entry.
     * A `useState` copy kept in step with this one is rule 31's banned shape, so the copies are deleted.
     *
     * The cost is that a section no longer remembers the sort you last used in it (Posts → Thoughts →
     * Posts is back at Newest). Accepted: that memory was invisible, unshareable and did not survive a
     * reload. Empty string for the sections that have no sort. §2.4 of
     * `designs/01-now/2026-08-11-header-count-chips-as-navigation-design.md`.
     */
    ordering: string;
    /**
     * FI-618: the owner is previewing their own profile as a visitor sees it (`?as=visitor`). A route
     * field rather than component state for the reason every field here is one: switching it changes
     * the address, so Back must undo it, and a `useState` copy beside this state would be gone by the
     * time Back lands. Meaningful only on the owner's own profile; everywhere else it is read and
     * ignored. Compared in `areEqual` like every other field.
     */
    asVisitor: boolean;
};

/**
 * The sort vocabulary is a property of the SECTION, so the section is what picks the normalizer.
 *
 * Every branch delegates to the existing `normalizeUser*Ordering` function rather than restating its
 * values, which is what makes an ordering that is meaningless for the section it arrives with — a
 * hand-typed URL, or a stale link from before a section's options changed — degrade to that section's
 * default instead of to a list nothing can render.
 */
function normalizeUserPageOrdering(section: UserPageSection, value?: string | null): string {
    switch (section) {
        case "discussions":
        case "images":
            return normalizeUserDiscussionOrdering(value ?? undefined, "authored");
        case "participated":
            return normalizeUserDiscussionOrdering(value ?? undefined, "participated");
        case "arguments":
            return normalizeUserArgumentOrdering(value ?? undefined, "authored");
        case "groups":
            return normalizeUserGroupOrdering(value ?? undefined);
        case "series":
            return normalizeUserSeriesOrdering(value ?? undefined);
        default:
            return "";
    }
}

const DEFAULT_USER_PAGE_SECTION: UserPageSection = "discussions";
const DEFAULT_USER_GROUP_SECTION: UserGroupSection = "leader";

/**
 * The profile's ONE address builder (`EMPTY-STATES-82`).
 *
 * <p>Module scope, and used by both `useHistoryBackedState`'s `buildUrl` and the count chips' `href`,
 * because the row's constraint is that the anchor and the pushed history entry must be built by one
 * function: two copies of this mapping is exactly how the address bar and the link come to disagree,
 * and the disagreement is silent — the chip navigates correctly while advertising the wrong address in
 * the status bar, in a hover preview and in "copy link address".
 *
 * <p>`MyDashboardPage` took the same decision on the same night for the same reason; its function is
 * `buildDashboardUrl`.
 *
 * <p>The default sort is omitted, so ordinary navigation produces the URLs this page always had and a
 * shared link carries a sort only when the sender actually chose one.
 */
function buildUserPageUrl(username: string, state: UserRouteState): string {
    // The wall has NO path segment, deliberately: §6.1's constraint 2 refuses one and §0.2 dropped the
    // `/notes` route this replaced. It is a query on the profile's own address, which keeps
    // `/user/{name}` the one canonical, indexed URL for a member while still giving the wall a
    // shareable address of its own — "not the default" must not mean "not reachable".
    // FI-618: the visitor preview rides every form of the address, so moving around the profile while
    // previewing stays in the preview, and a reload keeps it.
    const preview = state.asVisitor ? "as=visitor" : "";
    if (state.view === "wall") {
        const anchor = state.entry ? `&entry=${encodeURIComponent(state.entry)}` : "";
        return `/user/${encodeURIComponent(username)}?view=wall${anchor}${preview ? `&${preview}` : ""}`;
    }

    const isDefaultOrdering = state.ordering === normalizeUserPageOrdering(state.section);
    const queryParts = [
        state.ordering && !isDefaultOrdering ? `ordering=${encodeURIComponent(state.ordering)}` : "",
        preview,
    ].filter(Boolean);
    const query = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
    const groupSegment = state.section === "groups" ? `/${encodeURIComponent(state.groupSection)}` : "";

    return `/user/${encodeURIComponent(username)}/${encodeURIComponent(state.section)}${groupSegment}${query}`;
}

/**
 * The address's view, read from the location and NOT from what the page is currently showing.
 *
 * `?view=wall` and `?entry=` are explicit and win for anybody, logged in or not. With neither, a URL
 * naming a section means the sections view, and the bare `/user/{name}` falls back to the server's
 * viewer-dependent default (P9).
 */
function readViewFromLocation(location: { search: string }, pathNamesASection: boolean,
                              defaultView: UserMainColumnView): UserMainColumnView {
    const params = new URLSearchParams(location.search);
    if (params.get("view") === "wall" || params.get("entry")) {
        return "wall";
    }
    if (params.get("view") === "sections") {
        return "sections";
    }
    return pathNamesASection ? "sections" : defaultView;
}

/**
 * FI-618: the owner's "View as visitor" control, and, while previewing, the thin banner that says what
 * the page now is and offers the way out. Both halves are one component so the control and its exit can
 * never be shown together, or both missing, on the owner's profile.
 */
function ProfileVisitorPreviewBar({ previewing, onChange, localizer }: {
    previewing: boolean;
    onChange: (asVisitor: boolean) => void;
    localizer: Localizer;
}) {
    if (!previewing) {
        return (
            <div className="mb-2 flex justify-end">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-playwright-id="profile-visitor-preview-open"
                    onClick={() => onChange(true)}
                >
                    <Eye className="size-4" aria-hidden="true" />
                    {localizer("profile.visitor_preview.open")}
                </Button>
            </div>
        );
    }
    return (
        <div
            className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-border bg-muted px-3 py-1.5 text-sm text-text-main"
            data-playwright-id="profile-visitor-preview-banner"
        >
            <span className="min-w-0">{localizer("profile.visitor_preview.banner")}</span>
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                data-playwright-id="profile-visitor-preview-exit"
                onClick={() => onChange(false)}
            >
                {localizer("profile.visitor_preview.exit")}
            </Button>
        </div>
    );
}

function normalizeUserPageSection(section?: string | null): UserPageSection {
    return USER_PAGE_SECTIONS.includes((section ?? "") as UserPageSection)
        ? (section as UserPageSection)
        : DEFAULT_USER_PAGE_SECTION;
}

function normalizeUserGroupSection(section?: string | null): UserGroupSection {
    return USER_GROUP_SECTIONS.includes((section ?? "") as UserGroupSection)
        ? (section as UserGroupSection)
        : DEFAULT_USER_GROUP_SECTION;
}


type PaginationApi = ReturnType<typeof ClientFactory.getAjaxPaginationRestControllerApi>;

/**
 * The profile's list page size, mirroring `UserCollectionPaginationRepository.DEFAULT_PAGE_SIZE`.
 *
 * <p>Used only as `endMinItems` (`EMPTY-STATES-60`): below a full first page nothing was paginated, so
 * "End of list" is pagination machinery printed under a list that never paginated — rule 12 keeps that
 * out of user-facing copy, and the group page's leaders list took this decision by hand in 2026-07
 * with a written reason. Two adjacent surfaces answering the same question differently is how a copy
 * standard quietly stops being one; on the sparsest profile in the product it was the only closing
 * statement the page made about a person with two posts.
 */
const PROFILE_LIST_PAGE_SIZE = 10;

/**
 * The `filtered` rendering for the four profile lists that carry a language scope
 * (`EMPTY-STATES-32`).
 *
 * <p>When the scope is narrowed to `"mine"` — the viewer's reading set (the default until SV-78 ⑩,
 * round-29 A14, moved it to `"all"`) — the "N posts" chip beside it is still an unscoped server count. A visitor can therefore be shown fewer
 * posts than the profile claims exist, and in the sharp case none at all, under a list terminator
 * asserting the list is complete. "This person has published nothing" is the wrong sentence, and it is
 * the one the page was rendering.
 *
 * <p>This is the answer the product already chose on the owner's own dashboard, applied to the
 * surface that needed it more: the scope select is these lists' only narrowing control, so the flag
 * cannot be wrong, and every string here already exists in all 25 catalogs.
 */
function profileFilteredByLanguageState(localizer: Localizer, onClearFilter: () => void) {
    return (
        <PaginatedListEmptyState
            variant="filtered"
            icon={FilterX}
            titleHeadingLevel={2}
            title={localizer("paginated.filtered.title")}
            description={localizer("paginated.filtered.description")}
            action={{
                label: localizer("paginated.filtered.clear"),
                onClick: onClearFilter,
                icon: FilterX,
            }}
        />
    );
}

/**
 * `PROFILE-55` (S7 U54): the profile list search, the dashboard's round-38 D25 box reused. The narrowing
 * text is page state here, NOT a URL field: the profile's route state is the section, the sub-section, the
 * sort and the view, and a typed query is not an address a member navigates to (rule 33 stays untouched).
 * Trimmed and capped exactly like the dashboard's box and `CollectionQueryFilterSql`.
 */
const PROFILE_LIST_QUERY_MAX_LENGTH = 100;
const PROFILE_LIST_QUERY_DEBOUNCE_MS = 300;

function normalizeProfileListQuery(value?: string | null): string {
    return (value ?? "").trim().slice(0, PROFILE_LIST_QUERY_MAX_LENGTH);
}

/** The sections whose endpoint takes `?query=` (the dashboard's `supportsCollectionQuery`, public half). */
const PROFILE_QUERY_SECTIONS = new Set(["discussions", "images", "participated", "arguments", "series", "groups"]);

/** The list key's query segment: absent when there is nothing typed, so every existing key is unchanged. */
function listQueryKeySegment(listQuery?: string): string {
    return listQuery ? `:q:${listQuery}` : "";
}

/**
 * The `filtered` panel once a list can be narrowed by the search box as well as by the language scope.
 * A miss caused by the box ALONE on a posts list names the term and says "Clear search" (the dashboard's
 * D25 wording, existing keys); any other narrowing keeps the shared "your filters" trio, and its clear
 * releases BOTH controls, or the panel's own button would leave the list still empty.
 */
function profileFilteredState(localizer: Localizer, options: {
    listQuery?: string;
    languageNarrowed: boolean;
    onClearLanguageScope: () => void;
    onClearListQuery?: () => void;
    postsWording: boolean;
}) {
    const clearAll = () => {
        options.onClearLanguageScope();
        options.onClearListQuery?.();
    };
    if (options.listQuery && !options.languageNarrowed && options.postsWording) {
        return (
            <PaginatedListEmptyState
                variant="filtered"
                icon={Search}
                titleHeadingLevel={2}
                title={localizer("dashboard.search.no_match", options.listQuery)}
                action={{
                    label: localizer("groups.directory.clear_search"),
                    onClick: clearAll,
                    icon: X,
                }}
            />
        );
    }
    return profileFilteredByLanguageState(localizer, clearAll);
}

// These list sections are declared at module scope (not inside UserPage) so they keep a stable
// component identity across UserPage re-renders. UserPage re-renders on profile/follow/member
// events; if these lived in the function body, each render would mint a new component type and
// React would unmount/remount the PaginatedListWrapperV2 subtree — discarding loaded pages, the
// cursor dedup set, and scroll position, then refetching from page 1. The key={...} on each wrapper
// still encodes the intended remount triggers.

type DiscussionsListProps = {
    mode: "authored" | "participated" | "images";
    ordering: UserDiscussionOrderingStyle;
    username?: string;
    paginationApi: PaginationApi;
    discussionLanguageScope: UserDiscussionLanguageScope;
    onClearLanguageScope: () => void;
    localizer: Localizer;
    displayUsername: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    /** S7 U54: the search box's settled text ("" = no narrowing) and its clear. */
    listQuery?: string;
    onClearListQuery?: () => void;
};

function DiscussionsList(props: DiscussionsListProps) {
    const {
        mode,
        ordering,
        username,
        paginationApi,
        discussionLanguageScope,
        onClearLanguageScope,
        localizer,
        displayUsername,
        sensitiveMasking,
        listQuery = "",
        onClearListQuery,
    } = props;
    const fetchPage = React.useCallback(async (paginationParameter?: string) => {
        if (!username) return [] as DiscussionWithoutContentDTO[];
        try {
            return await fetchUserDiscussionsPage(paginationApi, {
                username,
                mode,
                orderingStyle: ordering,
                paginationParameter,
                languageScope: discussionLanguageScope,
                query: listQuery,
            });
        } catch (e) {
            logApiFailure("[UserPage] fetch discussions error", e);
            throw e;
        }
    }, [mode, ordering, discussionLanguageScope, listQuery, paginationApi, username]);

    const getId = React.useCallback((d: DiscussionWithoutContentDTO) => d?.publicId ?? undefined, []);

    const handleFeedPageShown = React.useCallback((info: {
        pageIndex: number;
        pageItems: DiscussionWithoutContentDTO[];
    }) => {
        reportFeedPageShown({
            feedType: "user",
            ordering,
            pageIndex: info.pageIndex,
            items: info.pageItems,
        });
    }, [ordering]);

    return (
        <PaginatedListWrapperV2<DiscussionWithoutContentDTO, string>
            key={`user:discussions:${mode}:${ordering}:${discussionLanguageScope}:${username ?? ""}${listQueryKeySegment(listQuery)}`}
            // Back-navigation restoration (SV-24). Mirrors `key=` exactly: the two must change together,
            // or a remount from a tab/ordering switch would restore the previous selection's pages, and a
            // key that omitted one of these parameters would restore the wrong tab's items.
            restorationKey={`user:discussions:${mode}:${ordering}:${discussionLanguageScope}:${username ?? ""}${listQueryKeySegment(listQuery)}`}
            fetchPage={fetchPage}
            pageSize={PROFILE_LIST_PAGE_SIZE}
            getId={getId}
            onPageShown={handleFeedPageShown}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <DiscussionCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                endMinItems: PROFILE_LIST_PAGE_SIZE,
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                // user.jte server-renders this same list as crawlable anchors inside the UserPage
                // island, so a terminal fetch failure must not leave the profile emptier than the
                // HTML already was (frontend F2, 2026-08-04).
                ssrFallback: crawlableDiscussionListFallback("UserPage"),
                isFiltered: discussionLanguageScope === "mine" || Boolean(listQuery),
                filtered: profileFilteredState(localizer, {
                    listQuery,
                    languageNarrowed: discussionLanguageScope === "mine",
                    onClearLanguageScope,
                    onClearListQuery,
                    postsWording: true,
                }),
                empty: (
                    mode === "images" ? (
                        // "No posts by {0} yet" would be false for a member who has posted without
                        // images, and no images-specific copy was settled (round 12 `2e`).
                        <PaginatedListEmptyState
                            titleHeadingLevel={2}
                            icon={ImageIcon}
                            title={localizer("paginated.empty.description")}
                        />
                    ) : (
                        // `PROFILE-71` (A37): the title alone. "Nothing to show here yet." restated the
                        // title in vaguer words on the page's most prominent panel; the branch above
                        // already renders its title with no description.
                        <PaginatedListEmptyState
                            titleHeadingLevel={2}
                            icon={mode === "participated" ? MessageCircle : MessageSquareText}
                            title={localizeWithMemberName(localizer, "user.page.empty.discussions.title", displayUsername)}
                        />
                    )
                )
            })}
            render={({ items }) => (
                <MainPageListStack className={MAIN_PAGE_LIST_EDGE_ON_PHONE_CLASS}>
                    {items.map((d, index) => (
                        <DiscussionCardDisplayer
                            key={d.publicId}
                            discussion={d}
                            // `PROFILE-81` (A40): on the member's OWN posts (Posts, Images) every
                            // card repeated the page subject's avatar and handle, spending ~52 CSS px of a
                            // phone card on a fact the header states. The Thoughts list's `DASHBOARD-15`
                            // rule, crossed here: Participated lists OTHER members' posts, so it keeps them.
                            showAuthor={mode === "participated"}
                            sensitiveMasking={sensitiveMasking}
                            feedAttribution={{ feedType: "user", ordering, position: index + 1 }}
                            localizer={localizer}
                        />
                    ))}
                </MainPageListStack>
            )}
        />
    );
}

type ArgumentsListProps = {
    username?: string;
    paginationApi: PaginationApi;
    argumentOrdering: UserThoughtOrderingStyle;
    localizer: Localizer;
    displayUsername: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    /** S7 U54: the search box's settled text and its clear. */
    listQuery?: string;
    onClearListQuery?: () => void;
};

function ArgumentsList(props: ArgumentsListProps) {
    const { username, paginationApi, argumentOrdering, localizer, displayUsername, sensitiveMasking, listQuery = "", onClearListQuery } = props;
    // The Thoughts tab is not language-scoped (see fetchUserArgumentsPage): always the author's arguments.
    const fetchPage = React.useCallback(async (paginationParameter?: string) => {
        if (!username) return [] as ThoughtInSearchDTO[];
        try {
            return await fetchUserArgumentsPage(paginationApi, {
                username,
                mode: "authored",
                orderingStyle: argumentOrdering,
                paginationParameter,
                query: listQuery,
            });
        } catch (e) {
            logApiFailure("[UserPage] fetch arguments error", e);
            throw e;
        }
    }, [argumentOrdering, listQuery, paginationApi, username]);

    const getId = React.useCallback((a: ThoughtInSearchDTO) => a?.publicId ?? undefined, []);

    return (
        <PaginatedListWrapperV2<ThoughtInSearchDTO, string>
            key={`user:arguments:${argumentOrdering}:${username ?? ""}${listQueryKeySegment(listQuery)}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`user:arguments:${argumentOrdering}:${username ?? ""}${listQueryKeySegment(listQuery)}`}
            fetchPage={fetchPage}
            pageSize={PROFILE_LIST_PAGE_SIZE}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <DiscussionCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                endMinItems: PROFILE_LIST_PAGE_SIZE,
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                // S7 U54: the Thoughts list has no language scope, so the search box is its only narrowing.
                isFiltered: Boolean(listQuery),
                filtered: onClearListQuery
                    ? profileFilteredByLanguageState(localizer, onClearListQuery)
                    : undefined,
                empty: (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={MessageSquareQuote}
                        title={localizeWithMemberName(localizer, "user.page.empty.thoughts.title", displayUsername)}
                        description={localizer("paginated.empty.description")}
                    />
                )
            })}
            render={({ items }) => (
                <MainPageListStack>
                    {/* `PROFILE-13` — this list is `mode: "authored"`, so every row is the profile
                        owner's own thought. With the defaults the card led with the CONTAINING
                        discussion's title as a `text-lg` headline, printed the discussion author's
                        byline above a second byline repeating the profile owner, and left two
                        unlabelled relative timestamps ~50px apart that diverge whenever a thought
                        was written later than the post it answers. On the one page whose subject is a
                        single member, the largest text in every row was a different member's title.

                        Not a new variant: these are the two props `MyDashboardPage` already passes
                        for the same kind of list, and `DASHBOARD-15` is the row that took the
                        decision. This is its uncrossed second call site.

                        `showAuthor={false}` is safe HERE and only here — `empty-states.md` records as
                        a deliberate decision that the REACTED thought tabs keep both, because those
                        are other people's thoughts. A public profile's Thoughts section is authored
                        only, so it falls on the other side of that same rule. */}
                    {items.map(a => (
                        <ThoughtCardDisplayer
                            key={getId(a) ?? `${a.id}`}
                            argument={a}
                            emphasis="thought"
                            showAuthor={false}
                            // `PROFILE-52` (S7 U53): a profile's thought list shows COUNTS ONLY, for every
                            // row and every viewer. The card's live control used to appear only on rows
                            // the viewer had already voted on (mount-captured vote context), so you could
                            // upvote from this list only if you already had. Voting stays on the thought.
                            showViewerReactions={false}
                            sensitiveMasking={sensitiveMasking}
                            localizer={localizer}
                        />
                    ))}
                </MainPageListStack>
            )}
        />
    );
}

type FollowListProps = {
    username?: string;
    paginationApi: PaginationApi;
    refreshKey: number;
    localizer: Localizer;
    displayUsername: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    /**
     * SV-78 ⑩ (round-33 A7): on the owner's own profile the empty tab speaks to the owner with the
     * copy `MyDashboardPage` already uses for the same list, instead of naming them in the third person.
     */
    isSelfView: boolean;
};

function FollowersList(props: FollowListProps) {
    const { username, paginationApi, refreshKey, localizer, displayUsername, sensitiveMasking, isSelfView } = props;
    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!username) return [] as UserProfileDTO[];
        try {
            return await fetchFollowersPage(paginationApi, username, cutoff);
        } catch (e) {
            logApiFailure("[UserPage] fetch followers error", e);
            throw e;
        }
    }, [paginationApi, username]);

    const getId = React.useCallback((u: UserProfileDTO) => u?.username ?? undefined, []);

    return (
        <PaginatedListWrapperV2<UserProfileDTO, string>
            key={`user:followers:${username ?? ""}:${refreshKey}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`user:followers:${username ?? ""}:${refreshKey}`}
            fetchPage={fetchPage}
            pageSize={PROFILE_LIST_PAGE_SIZE}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <ProfileCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                endMinItems: PROFILE_LIST_PAGE_SIZE,
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                // Owner view: the dashboard's record-only panel, with no action (DASHBOARD-57).
                empty: isSelfView ? (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={Users}
                        title={localizer("dashboard.empty.followers.title")}
                        description={localizer("dashboard.empty.followers.description")}
                    />
                ) : (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={Users}
                        title={localizeWithMemberName(localizer, "user.page.empty.followers.title", displayUsername)}
                        description={localizer("paginated.empty.description")}
                        // `PROFILE-53` (S7 A77): the one person standing in front of an empty followers
                        // list is the one who can change it, so the visitor's panel carries the SAME
                        // Follow control the header offers (`UserButtons`, primary-only: no Message, no
                        // menu). No new copy, no second follow path: the button owns its optimistic
                        // state, the account-access gate for a signed-out visitor, and the events that
                        // move the header count. The owner branch above still offers nothing (DASHBOARD-57).
                        action={username ? (
                            <UserButtons
                                username={username}
                                displayMode="primary-only"
                                surface="user_profile_followers_empty"
                                localizer={localizer}
                                align="start"
                            />
                        ) : undefined}
                    />
                )
            })}
            render={({ items }) => (
                <MainPageListStack>
                    {items.map(u => <UserCardDisplayer key={getId(u)} user={u} sensitiveMasking={sensitiveMasking} surface="followers_list" localizer={localizer} />)}
                </MainPageListStack>
            )}
        />
    );
}

function FollowingList(props: FollowListProps) {
    const { username, paginationApi, refreshKey, localizer, displayUsername, sensitiveMasking, isSelfView } = props;
    const fetchPage = React.useCallback(async (cutoff?: string) => {
        if (!username) return [] as UserProfileDTO[];
        try {
            return await fetchFollowingsPage(paginationApi, username, cutoff);
        } catch (e) {
            logApiFailure("[UserPage] fetch followings error", e);
            throw e;
        }
    }, [paginationApi, username]);

    const getId = React.useCallback((u: UserProfileDTO) => u?.username ?? undefined, []);

    return (
        <PaginatedListWrapperV2<UserProfileDTO, string>
            key={`user:following:${username ?? ""}:${refreshKey}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`user:following:${username ?? ""}:${refreshKey}`}
            fetchPage={fetchPage}
            pageSize={PROFILE_LIST_PAGE_SIZE}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <ProfileCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                endMinItems: PROFILE_LIST_PAGE_SIZE,
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                // Owner view: the dashboard's panel and its people-search action (EMPTY-STATES-46).
                empty: isSelfView ? (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={UserPlus2}
                        title={localizer("dashboard.empty.following.title")}
                        description={localizer("dashboard.empty.following.description")}
                        action={{
                            label: localizer("dashboard.empty.following.find_people"),
                            onClick: () => navigateToUrl("/search?searchType=users", { source: "profile-empty-following" }),
                            icon: Users,
                        }}
                    />
                ) : (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={Users}
                        title={localizeWithMemberName(localizer, "user.page.empty.following.title", displayUsername)}
                        description={localizer("paginated.empty.description")}
                    />
                )
            })}
            render={({ items }) => (
                <MainPageListStack>
                    {items.map(u => <UserCardDisplayer key={getId(u)} user={u} sensitiveMasking={sensitiveMasking} surface="following_list" localizer={localizer} />)}
                </MainPageListStack>
            )}
        />
    );
}

type SeriesListProps = {
    username?: string;
    paginationApi: PaginationApi;
    seriesOrdering: UserSeriesOrderingStyle;
    discussionLanguageScope: UserDiscussionLanguageScope;
    onClearLanguageScope: () => void;
    localizer: Localizer;
    displayUsername: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    /** SV-78 ⑩ (round-33 D20): the owner reads "No series yet", not their own name in the third person. */
    isSelfView: boolean;
    /** S7 U54: the search box's settled text and its clear. */
    listQuery?: string;
    onClearListQuery?: () => void;
};

function SeriesList(props: SeriesListProps) {
    const { username, paginationApi, seriesOrdering, discussionLanguageScope, onClearLanguageScope, localizer, displayUsername, sensitiveMasking, isSelfView, listQuery = "", onClearListQuery } = props;
    const fetchPage = React.useCallback(async (paginationParameter?: string) => {
        if (!username) return [] as SeriesDTO[];
        try {
            return await fetchUserSeriesPage(paginationApi, {
                ownerUsername: username,
                orderingStyle: seriesOrdering,
                visibility: "public",
                paginationParameter,
                languageScope: discussionLanguageScope,
                query: listQuery,
            });
        } catch (e) {
            logApiFailure("[UserPage] fetch public series error", e);
            throw e;
        }
    }, [discussionLanguageScope, listQuery, paginationApi, seriesOrdering, username]);

    const getId = React.useCallback((s: SeriesDTO) => s?.publicId ?? undefined, []);

    return (
        <PaginatedListWrapperV2<SeriesDTO, string>
            key={`user:series:${seriesOrdering}:${discussionLanguageScope}:${username ?? ""}${listQueryKeySegment(listQuery)}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`user:series:${seriesOrdering}:${discussionLanguageScope}:${username ?? ""}${listQueryKeySegment(listQuery)}`}
            fetchPage={fetchPage}
            pageSize={PROFILE_LIST_PAGE_SIZE}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            getVisibleItemsCount={(items) => filterBookmarksSeries(items).length}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <SeriesCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                endMinItems: PROFILE_LIST_PAGE_SIZE,
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                isFiltered: discussionLanguageScope === "mine" || Boolean(listQuery),
                filtered: profileFilteredState(localizer, {
                    listQuery,
                    languageNarrowed: discussionLanguageScope === "mine",
                    onClearLanguageScope,
                    onClearListQuery,
                    postsWording: false,
                }),
                empty: (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={ListPlus}
                        title={isSelfView
                            ? localizer("discussion.series.empty.title")
                            : localizeWithMemberName(localizer, "user.page.empty.series.title", displayUsername)}
                        description={localizer("paginated.empty.description")}
                    />
                )
            })}
            render={({ items }) => {
                const visibleSeries = filterBookmarksSeries(items);
                return (
                    <MainPageListStack>
                        {visibleSeries.map(s => <SeriesCardDisplayer key={getId(s)} series={s} sensitiveMasking={sensitiveMasking} localizer={localizer} />)}
                    </MainPageListStack>
                );
            }}
        />
    );
}

type GroupsListProps = {
    username?: string;
    paginationApi: PaginationApi;
    groupSection: UserGroupSection;
    groupOrdering: UserGroupOrderingStyle;
    groupMembershipRefreshKey: number;
    discussionLanguageScope: UserDiscussionLanguageScope;
    onClearLanguageScope: () => void;
    localizer: Localizer;
    displayUsername: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    /**
     * `PROFILE-45` (S7 A72, closes `DASHBOARD-12`'s open half): on the owner's own leader/member list
     * every card's "Add post" drops to the outline tier, exactly as `MyDashboardPage` already does for
     * its own collection. A visitor keeps the filled primary.
     */
    isSelfView: boolean;
    /** S7 U54: the search box's settled text and its clear. */
    listQuery?: string;
    onClearListQuery?: () => void;
};

function GroupsList(props: GroupsListProps) {
    const { username, paginationApi, groupSection, groupOrdering, groupMembershipRefreshKey, discussionLanguageScope, onClearLanguageScope, localizer, displayUsername, sensitiveMasking, isSelfView, listQuery = "", onClearListQuery } = props;
    const fetchPage = React.useCallback(async (paginationParameter?: string) => {
        if (!username) return [] as GroupDTO[];
        try {
            return await fetchUserGroupsPage(paginationApi, {
                username,
                scope: groupSection,
                orderingStyle: groupOrdering,
                visibility: "all",
                paginationParameter,
                languageScope: discussionLanguageScope,
                query: listQuery,
            });
        } catch (e) {
            logApiFailure("[UserPage] fetch visible groups error", e);
            throw e;
        }
    }, [discussionLanguageScope, groupOrdering, groupSection, listQuery, paginationApi, username]);

    const getId = React.useCallback((g: GroupDTO) => g?.publicId ?? undefined, []);
    // F13 (frontend review 2026-09-29): the handle goes in its own `translate="no"` span (FI-040 contract).
    const emptyTitle = groupSection === "leader"
        ? localizeWithMemberName(localizer, "user.page.empty.groups.leader.title", displayUsername)
        : localizeWithMemberName(localizer, "user.page.empty.groups.member.title", displayUsername);

    return (
        <PaginatedListWrapperV2<GroupDTO, string>
            key={`user:groups:${groupSection}:${groupOrdering}:${discussionLanguageScope}:${username ?? ""}:${groupMembershipRefreshKey}${listQueryKeySegment(listQuery)}`}
            // Back-navigation restoration (SV-24); mirrors `key=` — see the note on the discussions list.
            restorationKey={`user:groups:${groupSection}:${groupOrdering}:${discussionLanguageScope}:${username ?? ""}:${groupMembershipRefreshKey}${listQueryKeySegment(listQuery)}`}
            fetchPage={fetchPage}
            pageSize={PROFILE_LIST_PAGE_SIZE}
            getId={getId}
            className={MAIN_PAGE_LIST_WIDTH_CLASS}
            statusSlots={createStandardPaginatedStatusSlots({
                initialLoading: <ProfileCardListSkeleton />,
                loadingMessage: localizer("home.loading"),
                endMessage: localizer("home.end-of-list"),
                endMinItems: PROFILE_LIST_PAGE_SIZE,
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                isFiltered: discussionLanguageScope === "mine" || Boolean(listQuery),
                filtered: profileFilteredState(localizer, {
                    listQuery,
                    languageNarrowed: discussionLanguageScope === "mine",
                    onClearLanguageScope,
                    onClearListQuery,
                    postsWording: false,
                }),
                empty: (
                    <PaginatedListEmptyState
                        titleHeadingLevel={2}
                        icon={Users}
                        title={emptyTitle}
                        description={localizer("paginated.empty.description")}
                    />
                )
            })}
            render={({ items }) => (
                <MainPageListStack>
                    {items.map(g => <GroupCardDisplayer key={getId(g)} group={g} sensitiveMasking={sensitiveMasking} surface="user_profile" localizer={localizer} demotePrimaryAction={isSelfView} />)}
                </MainPageListStack>
            )}
        />
    );
}

export function UserPage(props: Props) {
    const {
        // No `= "discussions"` default any more: "the request named no section" and "the request named
        // discussions" are different facts, and only the first one may be decided by the landing rule
        // below. See `defaultSection`.
        initialSection,
        username,
        initiallyFollowing = false,
        initialUserProfilePictureUrl,
        initialUserDescription,
        initialUserProfileStatement,
        initialUserCreationDate,
        initialDiscussionsCount = null,
        initialArgumentsCount = null,
        initialAgreementsReceivedCount = null,
        initialMindsChangedReceivedCount = null,
        initialFollowersCount = null,
        initialFollowingCount = null,
        sensitive = false,
        hasInitialBootstrap = true,
        initialCanMessage = null,
        defaultMainColumnView,
        localizer
    } = props;
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const [profileImageUrl, setProfileImageUrl] = React.useState(initialUserProfilePictureUrl ?? null);
    const [profileDescription, setProfileDescription] = React.useState(initialUserDescription ?? null);
    const [profileStatement, setProfileStatement] = React.useState(initialUserProfileStatement ?? null);
    const [userSensitive, setUserSensitive] = React.useState(Boolean(sensitive));
    const [userIsViewerFollowing, setUserIsViewerFollowing] = React.useState(Boolean(initiallyFollowing));
    // D16 (safety plan Phase 1a): may THIS viewer start a conversation with this member? Seeded from
    // `user.jte`'s `data-can-message` (round 26), so an excluded viewer never sees the Message button on
    // first paint; `null` ("not asked for": the button renders as before) when there is no seed. The
    // bootstrap's answer is then applied on every load like `collectionCounts`. The seed belongs to the
    // username the server rendered, and only to it: a soft navigation to another profile starts at null.
    const [viewerCanMessage, setViewerCanMessage] = React.useState<boolean | null>(initialCanMessage);
    // Welcome hosts D3/A21: the "Welcome host" label flag. Bootstrap-only (no SSR seed, no `user.jte`
    // twin), applied on every load like canMessage, and reset on a soft navigation so one profile's
    // label never shows on the next.
    const [welcomeHost, setWelcomeHost] = React.useState(false);
    const canMessageSeedUsernameRef = React.useRef(username);
    const [userCreationDate, setUserCreationDate] = React.useState(initialUserCreationDate ?? null);
    const [discussionsCount, setDiscussionsCount] = React.useState<number | null>(initialDiscussionsCount ?? null);
    const [argumentsCount, setArgumentsCount] = React.useState<number | null>(initialArgumentsCount ?? null);
    const [agreementsReceivedCount, setAgreementsReceivedCount] = React.useState<number | null>(initialAgreementsReceivedCount ?? null);
    const [mindsChangedReceivedCount, setMindsChangedReceivedCount] = React.useState<number | null>(initialMindsChangedReceivedCount ?? null);
    const [followersCount, setFollowersCount] = React.useState<number | null>(initialFollowersCount ?? null);
    const [followingCount, setFollowingCount] = React.useState<number | null>(initialFollowingCount ?? null);
    /**
     * `PROFILE-20` / `EMPTY-STATES-97` / `EMPTY-STATES-99`, decision **D1** — the sizes of the four
     * sections that have no count chip.
     *
     * <p>No dataset seed, deliberately and on the server's terms: batch 1 computes these in the
     * profile-bootstrap path rather than inside `findUserProfileDTO`, because that projection also
     * serves this page's SSR render and the profile-view recorder, and folding six sub-selects into it
     * would put them on two hot paths to serve a third. So they arrive from the fetch below and are
     * `null` until it lands.
     *
     * <p>A late arrival costs NOTHING here, which is the property this placement has and a chip does
     * not: the numbers land inside a closed dropdown's option labels, so nothing moves.
     */
    const [collectionCounts, setCollectionCounts] = React.useState<UserCollectionCounts | null>(null);
    // FI-631: the three viewer-mutated lists' refresh tokens are module-level counters, not page state,
    // so they survive the remount Back performs and count a change made while the page was away (a
    // page-state counter reset to 0 and restored the pre-change cache entry). See `viewerMutationKey`.
    const followersRefreshKey = useViewerMutationKey(username ? `followers:${username}` : null);
    const followingRefreshKey = useViewerMutationKey(username ? `following:${username}` : null);
    const groupMembershipRefreshKey = useViewerMutationKey(username ? `group-membership:${username}` : null);
    const { revealed, reveal, sensitiveMasking } = useSensitivePageUnlock({
        kind: "user",
        id: username,
        isSensitive: userSensitive,
        // `PROFILE-22` (S7 U50): the owner opening their own profile is never gated. The same owner fact
        // as `isSelfView` below (read here because the hook runs first); the server renders no gate, so
        // there is no SSR half to disagree with.
        viewerOwnsPage: Boolean(username && loggedInUsername && username === loggedInUsername),
    });

    /**
     * The section this profile OPENS on (slice P1a).
     *
     * On an ordinary page load the SERVER has already decided — `user.jte` writes `data-section` from
     * `UserController.resolveLandingSection` and server-renders the crawlable list off the same value —
     * so this reads its answer and the two cannot disagree. That agreement is the whole point: an island
     * that picked its own default would replace the server's render one frame later with a different
     * section, and a crawler on a cold cache would index the discarded one.
     *
     * The resolver runs only when the mount was handed no section at all. It reads the **initial** counts
     * on purpose: resolving against the live ones would move the landing section when a bootstrap fetch
     * lands, which is the same mid-flight swap arriving a second late.
     *
     * The rule itself changed on 2026-08-19 (`EMPTY-STATES-12` / `PROFILE-10`): the LARGEST content
     * section wins rather than the first non-empty one. Nothing changes here — all three counts were
     * already being handed over — but this call site is what carries it, so the wiring is pinned in
     * `UserPage.spec.tsx` as well as the rule in `ProfileLandingSection.spec.ts`. Read the rule there.
     */
    const defaultSection = React.useMemo(() => normalizeUserPageSection(
        initialSection ?? resolveLandingSection({
            discussionsCount: initialDiscussionsCount,
            argumentsCount: initialArgumentsCount,
            followersCount: initialFollowersCount,
        }),
    ), [initialArgumentsCount, initialDiscussionsCount, initialFollowersCount, initialSection]);
    /**
     * P9's default, as the SERVER decided it. Absent meta means `sections`, which is the safe direction:
     * a member briefly seeing the posts hub is a smaller wrong than a crawler indexing a wall.
     */
    const defaultView: UserMainColumnView = defaultMainColumnView === "wall" ? "wall" : "sections";

    const initialRouteState = React.useMemo<UserRouteState>(() => ({
        // The server named a section only when the REQUEST did (`initialSection`); a resolved landing
        // section arrives through `defaultSection` and does not mean "the address is a sections URL".
        view: initialSection ? "sections" : defaultView,
        entry: "",
        section: defaultSection,
        groupSection: DEFAULT_USER_GROUP_SECTION,
        ordering: normalizeUserPageOrdering(defaultSection),
        asVisitor: false,
    }), [defaultSection, defaultView, initialSection]);

    const getSectionsFromPath = React.useCallback((pathname: string) => {
        const parts = pathname.split("/").filter(Boolean);
        const idx = parts.indexOf("user");
        if (idx === -1) {
            return {
                section: defaultSection,
                groupSection: DEFAULT_USER_GROUP_SECTION,
            };
        }

        const nextSection = normalizeUserPageSection(parts[idx + 2] ?? defaultSection);

        return {
            section: nextSection,
            groupSection: nextSection === "groups"
                ? normalizeUserGroupSection(parts[idx + 3])
                : DEFAULT_USER_GROUP_SECTION,
        };
    }, [defaultSection]);

    // Round 42 D14 (`MY-TOOLS-2`): `?tool=featured` from the `/my_tools` card, captured at the FIRST render
    // (a lazy initializer runs before any effect), because the `canonicalize` pass below rebuilds the
    // address from the route state and drops the param before the lazy showcase mounts. Read, never
    // written (rule 33).
    const [arrivedForFeaturedCuration] = React.useState(
        () => typeof window !== "undefined" && readMyToolsArrival(window.location.search) === "featured",
    );

    const { state: routeState, setState: setRouteState } = useHistoryBackedState<UserRouteState>({
        initialState: initialRouteState,
        readState: (location, fallback) => {
            const nextSections = getSectionsFromPath(location.pathname);
            const nextSection = nextSections.section ?? fallback.section;
            const params = new URLSearchParams(location.search);
            // "Does the PATH name a section" — not "did the resolver pick one". `/user/name` and
            // `/user/name/discussions` are different addresses and only the second one is a request.
            const pathNamesASection = location.pathname.split("/").filter(Boolean).length > 2;
            return {
                view: readViewFromLocation(location, pathNamesASection, defaultView),
                entry: params.get("entry") ?? "",
                section: nextSection,
                groupSection: nextSections.groupSection ?? fallback.groupSection,
                // Normalized against the section from the SAME location, never against the section the
                // page is currently showing: on a Back that changes both, the two must be read as one
                // pair or the sort is validated against the section it is leaving.
                ordering: normalizeUserPageOrdering(nextSection, params.get("ordering")),
                asVisitor: params.get("as") === "visitor",
            };
        },
        buildUrl: (state, location) => {
            if (!username) {
                return `${location.pathname}${location.search}${location.hash}`;
            }

            return buildUserPageUrl(username, state);
        },
        // ⚠️ EVERY field of the state is compared here, and that is rule 33 rather than thoroughness.
        // A field left out is treated as unchanged, the write is skipped, and the change silently
        // never happens — which for `entry` means a notification's deep link opens the wall and never
        // scrolls to the entry it was about, while every URL-shaped assertion about it still passes.
        areEqual: (left, right) => left.view === right.view
            && left.entry === right.entry
            && left.section === right.section
            && left.groupSection === right.groupSection
            && left.ordering === right.ordering
            && left.asVisitor === right.asVisitor,
    });
    const section = routeState.section;
    const groupSection = routeState.groupSection;
    const ordering = routeState.ordering;
    const displayUsername = React.useMemo(() => getDisplayUsername(username, localizer), [localizer, username]);
    // Defaults to ALL languages, like MyDashboardPage (SV-78 ⑩, round-29 A14): a profile that opened
    // narrowed to the viewer's reading set showed fewer posts than its own count claimed, and on the
    // owner's self-view it filtered their own content. The viewer can narrow to their languages.
    const [discussionLanguageScope, setDiscussionLanguageScope] = React.useState<UserDiscussionLanguageScope>("all");
    // The way out of the narrowed view, handed to the four lists the scope narrows (`EMPTY-STATES-32`).
    const clearDiscussionLanguageScope = React.useCallback(() => setDiscussionLanguageScope("all"), []);
    /**
     * `PROFILE-55` (S7 U54) — the list search. ONE state object scoped to the list it narrows: a box
     * typed on Posts must not narrow Series when the member switches, and deriving "empty" from a scope
     * mismatch needs no reset effect (so no second copy to keep in step, rule 31). `input` is what the box
     * shows, `query` the debounced value the lists fetch with; the only writer from `input` to `query` is
     * the debounce below, in one direction.
     */
    const listQueryScope = `${username ?? ""}:${section}:${groupSection}`;
    const [listQueryState, setListQueryState] = React.useState({ scope: listQueryScope, input: "", query: "" });
    const listQueryCurrent = listQueryState.scope === listQueryScope ? listQueryState : { scope: listQueryScope, input: "", query: "" };
    const listQueryInput = listQueryCurrent.input;
    const listQuery = listQueryCurrent.query;
    const setListQueryInput = React.useCallback((input: string) => {
        setListQueryState((previous) => previous.scope === listQueryScope
            ? { ...previous, input }
            : { scope: listQueryScope, input, query: "" });
    }, [listQueryScope]);
    const clearListQuery = React.useCallback(() => {
        setListQueryState({ scope: listQueryScope, input: "", query: "" });
    }, [listQueryScope]);
    React.useEffect(() => {
        const settled = normalizeProfileListQuery(listQueryInput);
        if (settled === listQuery) {
            return;
        }
        const timeout = window.setTimeout(() => {
            setListQueryState((previous) => previous.scope === listQueryScope && previous.query !== settled
                ? { ...previous, query: settled }
                : previous);
        }, PROFILE_LIST_QUERY_DEBOUNCE_MS);
        return () => window.clearTimeout(timeout);
    }, [listQuery, listQueryInput, listQueryScope]);
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);

    React.useEffect(() => {
        setProfileImageUrl(initialUserProfilePictureUrl ?? null);
        setProfileDescription(initialUserDescription ?? null);
        setProfileStatement(initialUserProfileStatement ?? null);
        setUserSensitive(Boolean(sensitive));
        setUserIsViewerFollowing(Boolean(initiallyFollowing));
        setUserCreationDate(initialUserCreationDate ?? null);
        setDiscussionsCount(initialDiscussionsCount ?? null);
        setArgumentsCount(initialArgumentsCount ?? null);
        setAgreementsReceivedCount(initialAgreementsReceivedCount ?? null);
        setMindsChangedReceivedCount(initialMindsChangedReceivedCount ?? null);
        setFollowersCount(initialFollowersCount ?? null);
        setFollowingCount(initialFollowingCount ?? null);
    }, [initialAgreementsReceivedCount, initialMindsChangedReceivedCount, initialArgumentsCount, initialDiscussionsCount, initialFollowersCount, initialFollowingCount, initialUserCreationDate, initialUserDescription, initialUserProfileStatement, initialUserProfilePictureUrl, initiallyFollowing, sensitive]);

    /**
     * The profile bootstrap. It now runs on EVERY load, not only after a client-owned mount.
     *
     * <p>`PROFILE-20` / D1. `user.jte` emits no `data-has-initial-bootstrap`, so `hasInitialBootstrap`
     * defaults to `true` and this effect never ran on the server-rendered profile — which is the same
     * trap `MyDashboardPage`'s own comment records having hit, and the reason its control row rendered
     * no counts for weeks. Every field the template DOES seed is still only overwritten when the seed
     * was missing (the `hasInitialBootstrap` guard moved inside), so nothing the server rendered is
     * replaced by a slower copy of itself. `collectionCounts` has no seed at all and is applied
     * unconditionally.
     *
     * <p>⚠️ This adds one GET to the profile page. It is the endpoint the page already called on a
     * soft navigation, it is anonymous-safe, and it is the cost D1's "the counts live on the profile
     * bootstrap" chose over putting six sub-selects on the SSR projection. It subtracts nothing from
     * the shipped HTML (rule 32): no section select waits on it, and a failure leaves the page exactly
     * as the server rendered it.
     */
    React.useEffect(() => {
        if (!username) {
            return;
        }

        let cancelled = false;
        // A soft navigation to another profile must not inherit the previous member's answer; the
        // server-rendered profile keeps its own seed until the bootstrap answers.
        setViewerCanMessage(username === canMessageSeedUsernameRef.current ? initialCanMessage : null);
        setWelcomeHost(false);
        ClientFactory.getUserPageBootstrap(username)
            .then((response) => {
                if (cancelled) {
                    return;
                }

                const bootstrap = response.data;
                const bootstrapWithFollowState = bootstrap as typeof bootstrap & {
                    collectionCounts?: UserCollectionCounts | null;
                    isViewerFollowing?: boolean | null;
                    discussionsAuthoredCount?: number | null;
                    argumentsAuthoredCount?: number | null;
                    agreementsReceivedCount?: number | null;
                    mindsChangedReceivedCount?: number | null;
                    followersCount?: number | null;
                    followingCount?: number | null;
                    // Added by the profile-destination seam; the generated client has not been
                    // regenerated for it yet, which is what the surrounding cast is already here for.
                    profileStatement?: string | null;
                };
                // The four sections with no chip. Applied on every load — see the effect's docblock.
                setCollectionCounts(bootstrapWithFollowState?.collectionCounts ?? null);
                // D16. Also applied on every load: the bootstrap's answer overrides the SSR seed, and a
                // bootstrap without the field ("not asked for") leaves whatever the seed said.
                if (typeof bootstrap?.canMessage === "boolean") {
                    setViewerCanMessage(bootstrap.canMessage);
                }
                // Welcome hosts: server-computed (hosting, label not hidden, 5 answered welcomes).
                setWelcomeHost(bootstrap?.welcomeHost === true);

                if (hasInitialBootstrap) {
                    // The server rendered these into the dataset on this very request, so they are not
                    // stale and re-applying them would only trade a correct value for a slower copy.
                    return;
                }

                setProfileImageUrl(bootstrap?.profileImageUrl ?? null);
                setProfileDescription(bootstrap?.description ?? null);
                setProfileStatement(bootstrapWithFollowState?.profileStatement ?? null);
                setUserSensitive(Boolean(bootstrap?.sensitive));
                setUserIsViewerFollowing(Boolean(bootstrapWithFollowState?.isViewerFollowing));
                setUserCreationDate(bootstrap?.creationDate ?? null);
                setDiscussionsCount(bootstrapWithFollowState?.discussionsAuthoredCount ?? null);
                setArgumentsCount(bootstrapWithFollowState?.argumentsAuthoredCount ?? null);
                setAgreementsReceivedCount(bootstrapWithFollowState?.agreementsReceivedCount ?? null);
                setMindsChangedReceivedCount(bootstrapWithFollowState?.mindsChangedReceivedCount ?? null);
                setFollowersCount(bootstrapWithFollowState?.followersCount ?? null);
                setFollowingCount(bootstrapWithFollowState?.followingCount ?? null);
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }

                logApiFailure("[UserPage] Failed loading user bootstrap", error, { username });
            });

        return () => {
            cancelled = true;
        };
    }, [hasInitialBootstrap, username]);

    React.useEffect(() => {
        if (!username) {
            return;
        }

        return onProfileUpdated((payload) => {
            if (!payload || payload.username !== username) {
                return;
            }

            if (payload.profileDescription !== undefined) {
                setProfileDescription(payload.profileDescription ?? null);
            }
            // `undefined` means the form that saved did not carry this field at all (the dashboard's
            // quick-edit dialog), so the value on screen is still correct and must not be cleared. Same
            // three-valued contract the endpoint honours.
            if (payload.profileStatement !== undefined) {
                setProfileStatement(payload.profileStatement ?? null);
            }
            if (payload.profileImageUrl !== undefined) {
                setProfileImageUrl(payload.profileImageUrl ?? null);
            }
            if (payload.sensitive !== undefined) {
                setUserSensitive(Boolean(payload.sensitive));
            }
        });
    }, [username]);

    React.useEffect(() => {
        const normalizedUsername = normalizeDisplayUsername(username) ?? username?.trim() ?? "";
        if (typeof window === "undefined" || !normalizedUsername || !window.location.pathname.startsWith("/user/")) {
            return;
        }

        const canonicalPath = `/user/${encodeURIComponent(normalizedUsername)}/${encodeURIComponent(section)}${section === "groups" ? `/${encodeURIComponent(groupSection)}` : ""}`;
        applyClientRouteDocumentState({
            title: normalizedUsername || localizer("literal.deleted_user"),
            canonicalUrl: new URL(canonicalPath, window.location.origin).href,
            description: profileDescription?.trim() || null,
            openGraphType: "profile",
        });
    }, [groupSection, localizer, profileDescription, section, username]);

    React.useEffect(() => {
        if (!username) {
            return;
        }

        const unsubscribeFollowAdded = onUserFollowAdded((payload) => {
            if (!payload) {
                return;
            }
            if (payload.targetUsername === username) {
                setFollowersCount((value) => (value === null ? value : value + 1));
                if (loggedInUsername && payload.actorUsername === loggedInUsername) {
                    setUserIsViewerFollowing(true);
                }
            }
            if (payload.actorUsername === username) {
                setFollowingCount((value) => (value === null ? value : value + 1));
            }
        });

        const unsubscribeFollowRemoved = onUserFollowRemoved((payload) => {
            if (!payload) {
                return;
            }
            if (payload.targetUsername === username) {
                setFollowersCount((value) => (value === null ? value : Math.max(0, value - 1)));
                if (loggedInUsername && payload.actorUsername === loggedInUsername) {
                    setUserIsViewerFollowing(false);
                }
            }
            if (payload.actorUsername === username) {
                setFollowingCount((value) => (value === null ? value : Math.max(0, value - 1)));
            }
        });

        return () => {
            unsubscribeFollowAdded();
            unsubscribeFollowRemoved();
        };
    }, [loggedInUsername, username]);


    React.useEffect(() => {
        if (!username) {
            return;
        }

        const tracker = new PageViewTracker({
            errorMessage: "Failed to record user profile view",
            context: { username },
            trackView: () => ClientFactory.countUserProfileView(username),
        });
        tracker.startViewTracking();

        return () => {
            tracker.stopViewTracking();
        };
    }, [username]);

    // `PROFILE-46` (S7 C38): the owner is addressed as "you", never narrated to by handle. Reads the
    // rendered owner fact, so the owner's visitor preview keeps the third-person labels a visitor sees.
    // Computed from the same inputs as `isSelfView` below (declared later, so restated here).
    const ownerViewForLabels = Boolean(username && loggedInUsername && username === loggedInUsername) && !routeState.asVisitor;
    const groupLeaderLabel = React.useMemo(
        () => ownerViewForLabels
            ? localizer("user.page.section.groups.leader.self")
            : localizer("user.page.section.groups.leader", displayUsername),
        [displayUsername, localizer, ownerViewForLabels],
    );
    const groupMemberLabel = React.useMemo(
        () => ownerViewForLabels
            ? localizer("user.page.section.groups.member.self")
            : localizer("user.page.section.groups.member", displayUsername),
        [displayUsername, localizer, ownerViewForLabels],
    );

    /**
     * `PROFILE-20` / `EMPTY-STATES-97` / `EMPTY-STATES-99` — decision **D1**, Form 1: the section's own
     * size, beside the section's own option.
     *
     * <p><b>A bare numeral after the product's own separator, not a worded plural.</b> The option
     * already names what is being counted ("Series"), so "Series · 3 series" would say it twice, and
     * Q8's decision explicitly permits a bare numeral beside an existing label. It also costs no
     * catalog key, which is what lets this ship in the same task as the render.
     *
     * <p><b>Absent is not zero, and a zero is not hidden.</b> `null` means the viewer may not see this
     * collection (or the number never arrived) and appends nothing at all; a real `0` renders, because
     * "Series · 0" is a true statement about the shape of this profile and is exactly the non-numeric
     * answer `EMPTY-STATES-99` asks for, one level down: it tells a visitor there is nothing there
     * without making them open the control to find out.
     */
    const withSectionCount = React.useCallback((label: string, count?: number | null) => (
        count == null ? label : `${label} · ${count}`
    ), []);

    const groupTabs = React.useMemo(() => ([
        // Two numbers, never summed onto one "groups" figure. A member who leads a group is also a row
        // in `group_members`, so a sum would be the size of neither list — `DASHBOARD-24`'s own defect
        // one layer down, and the reason batch 1 published two counts rather than one.
        { key: "leader", value: "leader", label: withSectionCount(groupLeaderLabel, collectionCounts?.groupsLeaderCount), icon: ShieldCheck },
        { key: "member", value: "member", label: withSectionCount(groupMemberLabel, collectionCounts?.groupsMemberCount), icon: Users },
    ]), [collectionCounts?.groupsLeaderCount, collectionCounts?.groupsMemberCount, groupLeaderLabel, groupMemberLabel, withSectionCount]);

    const isSelfView = Boolean(username && loggedInUsername && username === loggedInUsername);
    /**
     * FI-618: what the page RENDERS as the owner's view. The owner previewing as a visitor sees every
     * owner-only part give way, which is why every render below reads this and not `isSelfView`; the
     * preview control itself is the one thing that still reads `isSelfView`. A preview, never a
     * boundary: each endpoint still answers this member as the owner.
     */
    const ownerView = isSelfView && !routeState.asVisitor;
    const setVisitorPreview = React.useCallback((asVisitor: boolean) => {
        setRouteState((previous) => ({ ...previous, asVisitor }));
    }, [setRouteState]);

    /**
     * `PROFILE-20`, decision **D1**, Form 1 — the resting page states the profile's SHAPE instead of
     * hiding it behind an interaction.
     *
     * <p><b>Only the sections with no count chip carry a number</b>, and that is what keeps D1's third
     * fence ("no more chips") honest rather than merely obeyed. `buildStatBadges` already publishes
     * posts, thoughts, upvotes, minds-changed, followers and following; printing those figures here as
     * well would put the same number twice on one screen about 20 px apart, which is
     * `EMPTY-STATES-34`. What was missing — and what `PROFILE-20` is actually about — is that
     * `participated`, `groups` and `series` have no chip to be counted by at all.
     *
     * <p>`groups` is deliberately blank at this level: it has two sub-lists and its two numbers ride
     * the group sub-options, where each one is the size of the list it labels.
     *
     * <p>⛔ The other two fences are untouched here and must stay that way: the chips still navigate
     * (built 2026-08-11, pinned by `UserHeader.spec.tsx`), and no empty slot was added.
     */
    const sectionOptions = React.useMemo(() => {
        // `PROFILE-51` / `PROFILE-26` (S7 U51): three heading-less `section`s (what the member wrote,
        // who they know, what they gather) so the ten options read as three kinds of thing, divided by
        // SimpleSelect separators. No heading string: a separator costs no catalog key.
        const options = [
            // The eighth option, and the FIRST — it is the profile's default main-column view for a
            // logged-in member (P9), so it reads as where you start rather than as a trailing extra.
            //
            // ⚠️ `wall` is a picker VALUE and deliberately NOT a member of `USER_PAGE_SECTIONS`. That
            // tuple is the vocabulary of the profile's PATH SEGMENT — `buildUserPageUrl` renders
            // `/user/{name}/{section}` straight out of it — so adding `wall` there would mint
            // `/user/{name}/wall`, a new indexable URL of member-written praise. §6.1's constraint 2
            // refuses exactly that, and `noindex` is one-way in the safe direction. The wall's address
            // stays `?view=wall`. See §6.1.1 of the wall design.
            //
            // Offered to EVERY viewer, with no `defaultView` gate. That gate was the whole defect the
            // deleted `profile-wall-open` button carried: it rendered only when the server had already
            // chosen the wall, which it does only for a logged-in viewer, so an anonymous visitor had no
            // affordance at all while §9 promised "not the default is not not-reachable".
            { key: "wall", value: "wall", label: localizer("profile.wall.open"), icon: Sparkles, section: "content" },
            { key: "discussions", value: "discussions", label: localizer("dashboard.tab.discussions"), icon: MessageSquareText, section: "content" },
            // Round 12 `2e` (D30): the dashboard already calls this list "Images", so the profile reuses it.
            { key: "images", value: "images", label: localizer("dashboard.tab.images"), icon: ImageIcon, section: "content" },
            // `PROFILE-78` (V33, operator 2026-09-29): every option draws its own glyph. Participated shared
            // `MessageCircle` with Replies and Groups shared `Users` with Followers, so four of the options
            // could not be told apart by the scanning aid. Groups takes `UsersRound`, the glyph the group
            // surfaces already use for groups; Participated takes `MessageSquareReply`, free in this menu.
            { key: "participated", value: "participated", label: withSectionCount(localizer("dashboard.subtab.participated"), collectionCounts?.participatedCount), icon: MessageSquareReply, section: "content" },
            { key: "arguments", value: "arguments", label: localizer("dashboard.tab.thoughts"), icon: MessageSquareQuote, section: "content" },
            { key: "followers", value: "followers", label: localizer("discussion.section.followers"), icon: Users, section: "people" },
            { key: "following", value: "following", label: localizer("discussion.section.followings"), icon: UserPlus2, section: "people" },
            { key: "groups", value: "groups", label: localizer("dashboard.tab.groups"), icon: UsersRound, section: "collections" },
            { key: "series", value: "series", label: withSectionCount(localizer("dashboard.tab.series"), collectionCounts?.seriesCount), icon: List, section: "collections" },
        ];
        return options;
    }, [collectionCounts?.participatedCount, collectionCounts?.seriesCount, isSelfView, localizer, withSectionCount]);

    const headerOrderingOptions = React.useMemo<ProfilePageSelectOption[] | undefined>(() => {
        switch (section) {
            case "discussions":
            case "images":
                return getUserDiscussionOrderingOptions(localizer, "authored");
            case "participated":
                return getUserDiscussionOrderingOptions(localizer, "participated");
            case "arguments":
                return getUserArgumentOrderingOptions(localizer, "authored");
            case "groups":
                return getUserGroupOrderingOptions(localizer, groupSection, "publicProfile");
            case "series":
                return getUserSeriesOrderingOptions(localizer);
            default:
                return undefined;
        }
    }, [groupSection, localizer, section]);

    /**
     * `PROFILE-63` (A33): the size of the open section when the page knows it, else `null`. Every count
     * here is the size of the list it labels (the header chips and `collectionCounts`), so it is an upper
     * bound on what the list can show; Images publishes none and stays unknown.
     */
    const openSectionItemCount = React.useMemo<number | null>(() => {
        switch (section) {
            case "discussions":
                return discussionsCount;
            case "arguments":
                return argumentsCount;
            case "participated":
                return collectionCounts?.participatedCount ?? null;
            case "series":
                return collectionCounts?.seriesCount ?? null;
            case "groups":
                return (groupSection === "leader"
                    ? collectionCounts?.groupsLeaderCount
                    : collectionCounts?.groupsMemberCount) ?? null;
            default:
                return null;
        }
    }, [argumentsCount, collectionCounts, discussionsCount, groupSection, section]);

    /**
     * A sort cannot reorder fewer than two items, so the control is withheld rather than offered as a
     * menu whose every choice leaves the page unchanged. Only the CONTROL goes: `ordering` stays in the
     * route, so the address a member arrived on is neither dropped nor rewritten.
     */
    const visibleOrderingOptions = openSectionItemCount !== null && openSectionItemCount < 2
        ? undefined
        : headerOrderingOptions;

    const headerOrderingValue = ordering || undefined;

    /**
     * Re-sorting PUSHES the history entry (§13.1) — the same rule `SeriesPage` and `MyDashboardPage`
     * state, all three converted together on 2026-08-22. The sort lands in the URL, so it is a member
     * action that changes the address, and Back returns to the previous sort.
     */
    const handleHeaderOrderingChange = React.useCallback((value: string) => {
        setRouteState((previous) => ({
            ...previous,
            ordering: normalizeUserPageOrdering(previous.section, value),
        }));
    }, [setRouteState]);

    /**
     * A visitor looking at a member with an EMPTY wall is shown the posts hub instead.
     *
     * "This person has received nothing" is a negative displayed about somebody else, which the
     * achievements design already banned once — an empty trophy case reads as an accusation on a new
     * profile, which is why the rail is hidden entirely until the first award. The owner still gets
     * their own empty state, with the invitation in it; only a stranger is spared the box.
     *
     * ⚠️ A `replace`, not a push, and it needs no `HistoryReplaceReason` because it is not a history
     * write at all — it is a `useState` mirror consulted only while the address says nothing about the
     * view. Writing the route here would put an entry in the member's history that they never asked
     * for, and Back would land them on a wall the page had already decided not to show.
     */
    const [wallIsEmpty, setWallIsEmpty] = React.useState(false);
    const handleWallEmptiness = React.useCallback((isEmpty: boolean) => {
        setWallIsEmpty(isEmpty);
    }, []);

    const pageWarningCopy = React.useMemo(
        () => getPageContentWarningCopy(localizer, { isSensitive: userSensitive, kind: "user" }),
        [localizer, userSensitive],
    );

    /**
     * The handle the consent gate names as its subject (`PROFILE-4`).
     *
     * Same shape `UserHeader` prints: `@handle` for a real account, and the localized deleted-user
     * label bare — `@[Deleted User]` would be a handle nobody has.
     */
    const gateSubjectLabel = React.useMemo(() => {
        const normalizedUsername = normalizeDisplayUsername(username);
        return normalizedUsername ? `@${normalizedUsername}` : displayUsername;
    }, [displayUsername, username]);

    /**
     * The header's count chips are the entry points to the sections they count.
     *
     * Upvotes and minds-changed are counts of what this member RECEIVED, and there is no list of
     * received reactions — what there is is the thoughts they landed on, so both resolve there. Upvotes
     * additionally re-sorts to `agreements` so the click lands on the thoughts that earned the number
     * rather than on whatever is newest. That ordering is plain component state, not part of the route,
     * so Back returns the section and not the sort; the sort control is right there, and putting a
     * non-URL control into the history contract would hand it to rule 33 for no gain.
     *
     * Destinations table: 2026-08-11-header-count-chips-as-navigation-design.md §2.1.
     */
    const countNavigateDestination = React.useCallback((target: UserHeaderCountTarget) => {
        const nextSection: UserPageSection = target === "agreements" || target === "minds-changed"
            ? "arguments"
            : target;

        // ONE history entry carrying the section AND the sort, which is the whole reason the sort is in
        // the route: the upvotes chip changes both, and one Back has to undo both. A push, not a
        // replace — unlike re-sorting, a chip is a destination.
        //
        // No chip targets the groups section, so the sub-section resets exactly as `onSectionChange`
        // resets it on any other move away from groups.
        return {
            // A chip is a destination in the SECTIONS view by construction: every one of them counts a
            // list, and none of them counts anything the wall renders.
            view: "sections" as UserMainColumnView,
            entry: "",
            section: nextSection,
            groupSection: DEFAULT_USER_GROUP_SECTION,
            ordering: normalizeUserPageOrdering(nextSection, target === "agreements" ? "agreements" : undefined),
            // FI-618: a chip followed inside the visitor preview stays in it.
            asVisitor: routeState.asVisitor,
        };
    }, [routeState.asVisitor]);

    const handleCountNavigate = React.useCallback((target: UserHeaderCountTarget) => {
        setRouteState(countNavigateDestination(target));
    }, [countNavigateDestination, setRouteState]);

    /**
     * `EMPTY-STATES-82` — the address each count chip resolves to, so it can be a real link.
     *
     * <p>`MyDashboardPage` gained this on 2026-08-24 and the public profile renders the identical chips
     * from the identical component; this is the row's uncrossed second call site, and the row says so in
     * its own Status line ("it needs exactly one prop, `countHrefFor`, and nothing else").
     *
     * <p>Built from `countNavigateDestination` through `buildUserPageUrl` — the same destination the
     * click pushes, through the same builder `buildUrl` uses — so the href and the history entry are
     * one string by construction rather than by two mappings kept in step.
     *
     * <p>`undefined` while the username is not known: `CardMetadataBadges` keeps the `<button>` it had,
     * so the anchor is strictly an upgrade and never a requirement.
     */
    const countHrefFor = React.useCallback(
        (target: UserHeaderCountTarget) => (username ? buildUserPageUrl(username, countNavigateDestination(target)) : undefined),
        [countNavigateDestination, username],
    );

    /**
     * `EMPTY-STATES-85` — the chips that have nowhere to go from here.
     *
     * The row was filed on the dashboard's relationships tabs, and this page's `handleCountNavigate`
     * has the byte-identical shape: on `/user/X` already showing Thoughts at the default sort, the
     * thoughts chip resolves to the state the page is in, the URL guard skips the write, and the
     * `<button>` — wearing a resting chevron on every phone since `EMPTY-STATES-59` — does nothing.
     * Fixing one host and not its twin is exactly the uncrossed-second-call-site pattern this wave
     * is closing out.
     *
     * DERIVED from `countNavigateDestination` so the two cannot drift, and compared on the ordering
     * too: the upvotes chip re-sorts as well as switching section, so it stays a real control until
     * the page is already sorted that way.
     */
    const inertCountTargets = React.useMemo(
        () => USER_HEADER_COUNT_TARGETS.filter((target) => {
            const destination = countNavigateDestination(target);
            // `view` is part of the comparison: on the wall every chip is a real move (it changes the
            // main column), so none of them may be reported inert just because the section underneath
            // happens to match.
            return destination.view === routeState.view
                && destination.section === section
                && destination.groupSection === groupSection
                && destination.ordering === ordering;
        }),
        [countNavigateDestination, routeState.view, section, groupSection, ordering],
    );

    /**
     * What the main column ACTUALLY shows, which is not always what the route asked for.
     *
     * The one case they differ: the route says `wall`, the wall came back with nothing, and the viewer
     * is not the owner. `ProfileWall` renders `null` there (never display a negative about somebody
     * else), so without this the main column would be BLANK — which is rule 32's subtraction, arriving
     * through an empty state rather than through a crash. Derived, never a second stored copy of the
     * view: rule 31's ban on keeping one value in two places applies to a `useState` mirror of route
     * state exactly as it applies to a store.
     */
    const effectiveView: UserMainColumnView =
        routeState.view === "wall" && wallIsEmpty && !ownerView ? "sections" : routeState.view;

    /**
     * `PROFILE-79` (round 41 V34 a, with `PROFILE-53`). A SHORT section (1 to 3 items by the page's own
     * count) ends with one quiet link to the member's largest OTHER content section, so a visitor who
     * opened Thoughts on a member with one thought and 250 posts is not left at blank space with Back as
     * the only way on (T6). The link text is that section's EXISTING picker label (G-03): no new copy.
     *
     * <p>Only the four content sections compete (people and groups are not "more of this member's
     * writing"), only a strictly larger one wins, and only for a visitor in the sections view: the owner
     * knows their own profile, an EMPTY section is `PROFILE-53`/`PROFILE-71`'s empty state, and a count the
     * page does not know (Images) offers nothing. The destination is the route state a count chip pushes
     * (one history entry, rule 33), and the href is built from it, so the two cannot disagree.
     */
    const shortSectionHandOn = React.useMemo(() => {
        if (ownerView || effectiveView === "wall" || !username) {
            return null;
        }
        if (openSectionItemCount === null || openSectionItemCount < 1 || openSectionItemCount > 3) {
            return null;
        }
        const candidates: Array<{ section: UserPageSection; count: number | null; label: string }> = [
            { section: "discussions", count: discussionsCount, label: localizer("dashboard.tab.discussions") },
            { section: "arguments", count: argumentsCount, label: localizer("dashboard.tab.thoughts") },
            { section: "participated", count: collectionCounts?.participatedCount ?? null, label: localizer("dashboard.subtab.participated") },
            { section: "series", count: collectionCounts?.seriesCount ?? null, label: localizer("dashboard.tab.series") },
        ];
        let best: { section: UserPageSection; count: number; label: string } | null = null;
        for (const candidate of candidates) {
            if (candidate.section === section || candidate.count === null || candidate.count <= openSectionItemCount) {
                continue;
            }
            if (!best || candidate.count > best.count) {
                best = { section: candidate.section, count: candidate.count, label: candidate.label };
            }
        }
        if (!best) {
            return null;
        }
        const destination: UserRouteState = {
            view: "sections",
            entry: "",
            section: best.section,
            groupSection: DEFAULT_USER_GROUP_SECTION,
            ordering: normalizeUserPageOrdering(best.section),
            asVisitor: routeState.asVisitor,
        };
        return { section: best.section, label: best.label, destination, href: buildUserPageUrl(username, destination) };
    }, [argumentsCount, collectionCounts, discussionsCount, effectiveView, localizer, openSectionItemCount, ownerView, routeState.asVisitor, section, username]);

    // ONE props object for both halves of the header. In the rail layout the identity card and the
    // control row are two components in two grid cells, and the thing that must not drift between them
    // is which controls this section shows — so they read it from the same object rather than from two
    // call sites that have to be kept in step. UserHeaderProps types it.
    // `SV-134` — see the comment beside `{avatarLightbox}` at the foot of this component.
    const { openZoom: openAvatarZoom, lightbox: avatarLightbox } = useThumbnailLightbox({ localizer, site: "profile" });

    /**
     * `PROFILE-28` / `PROFILE-29` / `PROFILE-54` (S7 U47 ⚑G-04). The section the BARE profile opens on,
     * from the same resolver and the same initial counts as `defaultSection` — but never from the
     * request's own section, because a deep link to `/groups/leader` IS the member asking for a section,
     * and that is exactly the case whose first phone screen was 1.5 viewports of identity card.
     */
    const landingSection = React.useMemo(() => normalizeUserPageSection(resolveLandingSection({
        discussionsCount: initialDiscussionsCount,
        argumentsCount: initialArgumentsCount,
        followersCount: initialFollowersCount,
    })), [initialArgumentsCount, initialDiscussionsCount, initialFollowersCount]);
    const compactIdentityOnPhone = routeState.view === "sections" && section !== landingSection;

    const headerProps: UserHeaderProps = {
        username,
        localizer,
        compactOnPhone: compactIdentityOnPhone,
        initialProfileImageUrl: profileImageUrl,
        // The header hands back the URL it is CURRENTLY rendering, which after an in-place picture
        // change is not the one in `profileImageUrl` above.
        onAvatarClick: (imageUrl) => openAvatarZoom(imageUrl ?? profileImageUrl, displayUsername),
        initialDescription: profileDescription,
        initialCreationDate: userCreationDate ?? null,
        // What the column is actually SHOWING, which is what a control must say. `effectiveView` and not
        // `routeState.view`: an empty wall on a stranger's profile already falls back to the hub, and a
        // picker reading "Wall" over a list of posts would be describing a state the page declined.
        section: effectiveView === "wall" ? "wall" : section,
        onSectionChange: (value) => setRouteState((previous) => {
            // The eighth option is the wall, and it is the ONLY branch here. Everything else takes the
            // path it always took. Both halves are written in ONE setRouteState on purpose: rule 33's
            // assertions at UserPage.spec.tsx:989 and :999-1011 require that leaving the wall costs
            // exactly one history entry, so view and section must never be two writes. Branching the
            // STATE the handler writes is the fix; relaxing the depth expectation would not be.
            if (value === "wall") {
                // Clearing `entry` for the same reason the deleted open button did: arriving from a
                // notification deep link and then choosing Wall is a member asking for the wall, not for
                // the row they were sent to, and keeping the anchor would re-highlight a read entry
                // every time they came back.
                return { ...previous, view: "wall", entry: "" };
            }
            const nextSection = normalizeUserPageSection(value);
            return {
                // Picking any of the other seven is how a member leaves the wall.
                view: "sections",
                entry: "",
                section: nextSection,
                groupSection: nextSection === "groups" ? previous.groupSection : DEFAULT_USER_GROUP_SECTION,
                // The new section's own default, not the outgoing sort: the vocabularies do not overlap,
                // so carrying "agreements" from Thoughts into Posts would normalize to Newest anyway —
                // this just says so in one place instead of relying on the normalizer to absorb it.
                ordering: normalizeUserPageOrdering(nextSection),
                asVisitor: previous.asVisitor,
            };
        }),
        sectionOptions,
        secondarySection: section === "groups" ? groupSection : undefined,
        onSecondarySectionChange: section === "groups" ? (value: string) => setRouteState((previous) => ({
            ...previous,
            groupSection: normalizeUserGroupSection(value),
        })) : undefined,
        secondaryOptions: section === "groups" ? groupTabs : undefined,
        orderingValue: headerOrderingValue,
        onOrderingChange: visibleOrderingOptions ? handleHeaderOrderingChange : undefined,
        orderingOptions: visibleOrderingOptions,
        orderingLabel: localizer("profile.ordering.label"),
        // The owner's door to the recovery list, which lives on the dashboard and has no link anywhere
        // else. "Mi perfil" is where a member goes looking for their own posts: on 2026-08-14 one hit
        // this page's bootstrap five times hunting for a poem he had deleted, and never reached
        // /my_dashboard/discussions/deleted. Owner-only via `isSelfView` — a visitor must not learn that
        // this author has deleted posts, and the list itself is owner-clamped server-side regardless.
        //
        // `PROFILE-38` (S7 A71): the language scope is a NARROWING control, so it rides the foldable slot
        // and folds behind the header's one coarse-pointer "Filters" trigger with the ordering select,
        // exactly as `MyDashboardPage` does (`DASHBOARD-44`). The deleted-content pill stays in
        // `extraControls`: it is a ROUTE, not a filter, and folding it away re-creates `DASHBOARD-6`.
        //
        // `PROFILE-55` (S7 U54): the list search box leads the row on every section whose endpoint takes
        // `?query=`, the dashboard's D25 box and its existing copy (`header.tooltip.search`,
        // `groups.directory.clear_search`). It is not folded: it is how a member finds something, not a
        // filter they tune, and the dashboard keeps it inline for the same reason.
        extraControls: PROFILE_QUERY_SECTIONS.has(section) || (ownerView && section === "discussions") ? (
            <>
                {PROFILE_QUERY_SECTIONS.has(section) ? (
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-text" aria-hidden={true} />
                        <input
                            type="search"
                            value={listQueryInput}
                            onChange={(event) => setListQueryInput(event.target.value)}
                            maxLength={PROFILE_LIST_QUERY_MAX_LENGTH}
                            placeholder={localizer("header.tooltip.search")}
                            aria-label={localizer("header.tooltip.search")}
                            className={cn(
                                "h-9 w-40 rounded-full border border-border bg-card pl-9 pr-8 text-xs text-foreground sm:w-56 sm:text-sm",
                                // The iOS zoom floor: Safari zooms a focused control under 16px.
                                "[@media(pointer:coarse)]:text-base",
                                "placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                            )}
                            data-playwright-id="profile-list-query-input"
                        />
                        {listQueryInput ? (
                            <button
                                type="button"
                                onClick={clearListQuery}
                                aria-label={localizer("groups.directory.clear_search")}
                                className="standard-hover absolute right-2 top-1/2 touch-hit-44 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-neutral-text"
                                data-playwright-id="profile-list-query-clear"
                            >
                                <X className="size-3.5" aria-hidden={true} />
                            </button>
                        ) : null}
                    </div>
                ) : null}
                {ownerView && section === "discussions" ? (
                    <DeletedContentEntryPointButton
                        kind="discussion"
                        localizer={localizer}
                        source="profile-deleted-entry-point"
                    />
                ) : null}
            </>
        ) : undefined,
        collapsibleControls: (section === "discussions" || section === "images" || section === "participated" || section === "groups" || section === "series") ? (
            <DiscussionLanguageScopeSelect
                value={discussionLanguageScope}
                onChange={setDiscussionLanguageScope}
                localizer={localizer}
            />
        ) : undefined,
        collapsibleFilters: true,
        // The fold starts OPEN when the list is already narrowed, so the control that says so is never
        // the one missing. "mine" is the narrowing value, the same predicate the filtered empty state reads.
        filtersActive: discussionLanguageScope === "mine",
        // The trophy rail lives INSIDE the header card (design §9.1), mounting over the
        // server-rendered chip row in the same position — rule 32. It self-hides until the first
        // award, so a new profile gets no extra row.
        achievementsSlot: username ? <AchievementsRailLazy username={username} localizer={localizer} /> : undefined,
        // Slice F3's owner statement, INSIDE the identity card, mounting over the twin `user.jte`
        // already server-rendered in the same position (rule 32). `isSelfView` is the same fact the
        // template reads as `viewerIsOwner` — the meta tag it derives from is emitted by the very
        // response that rendered the template, so the two cannot disagree, and a third copy of "is this
        // mine" in the DOM would only be a third thing to drift.
        // `PROFILE-50` (S7 U50): the owner's badge leads to the setting that set it; a visitor's does not.
        sensitiveBadgeHref: ownerView ? PROFILE_SETTINGS_PATH : undefined,
        statementSlot: (
            <ProfileStatement
                localizer={localizer}
                statement={profileStatement}
                viewerIsOwner={ownerView}
            />
        ),
        // Slice F1 — the owner's pinned items and surfaced stances, BELOW the identity card in the rail
        // column (design §8). A section inside this island, never a second `data-component`: `user.jte`
        // is the exact template of the 2026-07-28 nested-island defect, where `createRoot()` deletes the
        // nested container the instant the parent chunk resolves. It renders `null` until there is
        // something to show, so a profile with nothing featured gains no DOM at all.
        showcaseSlot: username ? (
            <ProfileShowcaseLazy
                username={username}
                localizer={localizer}
                previewAsVisitor={isSelfView && !ownerView}
                openCurationOnArrival={arrivedForFeaturedCuration}
            />
        ) : undefined,
        // Newsletter growth D13 (slice 2c): one card per public newsletter, below the showcase. Inside this
        // island like the showcase (never a second `data-component`), and `null` for a member with none.
        newsletterSlot: username ? (
            <ProfileNewsletterCards
                username={username}
                localizer={localizer}
                viewerIsOwner={ownerView}
                loggedIn={Boolean(loggedInUsername)}
            />
        ) : undefined,
        // P12, owner-only. `isSelfView` decides whether to ASK; the authorization boundary is the
        // endpoint, which answers 403 to anyone who is not this member. The block self-hides while
        // loading and on failure, so a visitor's profile is unchanged and the owner's rail closes up.
        visitorInsightSlot: username && ownerView
            ? <ProfileVisitorInsights username={username} localizer={localizer} />
            : undefined,
        initiallyFollowing: userIsViewerFollowing,
        canMessage: viewerCanMessage,
        welcomeHost,
        sensitive: userSensitive,
        discussionsCount,
        argumentsCount,
        agreementsReceivedCount,
        mindsChangedReceivedCount,
        followersCount,
        followingCount,
        onCountNavigate: handleCountNavigate,
        countHrefFor,
        inertCountTargets,
    };

    return (
        <SensitivePageGate
            isSensitive={userSensitive}
            hideSensitiveMedia={MetaHandler.getHideSensitiveMedia()}
            revealed={revealed}
            onReveal={reveal}
            localizer={localizer}
            // PROFILE-4: whose profile this is, in the `@handle` form the profile header itself uses.
            // The gate blurs the header, so without this the only identifying text on screen is the URL.
            subjectLabel={gateSubjectLabel}
            badgeLabel={pageWarningCopy.badgeLabel}
            title={pageWarningCopy.title}
            helperText={pageWarningCopy.helperText}
            revealText={pageWarningCopy.revealText}
            showEnablePreferenceButton={pageWarningCopy.showSensitivePreferenceAction}
            revealButtonDataPlaywrightId="user-page-sensitive-reveal"
        >
            <PageWithRailLayout
                railFirst
                /* `PROFILE-17`, 2026-08-23. The identity block — avatar, `@handle`, the count chips,
                   Direct message, Follow, overflow — lives in the rail and ends 454px down a 2,648px
                   page, leaving a 384 x ~2,194px empty column, 27% of the viewport width, for the
                   whole of the rest of the scroll. Past the first screen a visitor reading someone's
                   thoughts has no reminder of whose profile they are on and no reachable Follow or
                   Direct message, which are the page's two conversion actions.

                   `DASHBOARD-20` took this decision on the dashboard on 2026-08-22 and this is its
                   uncrossed second call site — on the surface where the identity question actually
                   matters to a stranger. The prop carries its own guard
                   (`lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto`), so a tall card scrolls inside
                   itself rather than putting its lower half out of reach; and it is `lg:`-only, so
                   below the breakpoint the rail is still a normal block above the list. */
                railSticky
                dataPlaywrightId="user-page"
                innerClassName="py-4 mb-2"
                gridClassName="items-start"
                rail={(
                    <>
                        {/* FI-618: the owner's switch into the visitor preview, and the preview's own
                            banner with its way out. Above the identity card, so on a phone (rail first)
                            it is the first thing on the page while previewing. */}
                        {isSelfView ? (
                            <ProfileVisitorPreviewBar
                                previewing={!ownerView}
                                onChange={setVisitorPreview}
                                localizer={localizer}
                            />
                        ) : null}
                        <UserHeader {...headerProps} layout="rail" />
                    </>
                )}
                main={(
                    /* `data-section` here is the LIVE section, unlike the one on the page root — that
                       one is server-rendered from the request path and never changes again, so a spec
                       reading it sees the section the page was opened at rather than the one it is
                       showing. This attribute is set from the same `section` that selects which list
                       renders below, so it cannot be right while the list is wrong. */
                    <div className={`min-w-0 w-full ${MAIN_PAGE_LIST_WIDTH_CLASS}`} data-playwright-id="user-main-feed" data-section={section}>
                        {/* One measure for the whole column, matching the group page: the lists cap
                            themselves at MAIN_PAGE_LIST_WIDTH_CLASS, so the controls above them have to
                            share it or every card sits inset from the row that labels it. */}
                        <UserHeaderControls {...headerProps} layout="rail" />


                        {/* The wall (phase 2). A SECTION INSIDE this island, never a second island:
                            `user.jte` has exactly one `data-component` container and `createRoot()`
                            discards its element's children, so a nested one is deleted the instant the
                            parent chunk resolves — and it presents as flakiness, not absence. */}
                        {routeState.view === "wall" && username && (
                            <div className="w-full">
                                <ProfileWall
                                    username={username}
                                    localizer={localizer}
                                    highlightedEntryKey={routeState.entry || null}
                                    onEmptinessResolved={handleWallEmptiness}
                                    previewAsVisitor={isSelfView && !ownerView}
                                />
                            </div>
                        )}

                        {/* The `profile-wall-open` button that used to sit here is GONE (§6.1, reversed
                            2026-09-05). The way back to the wall is now the eighth entry in the section
                            picker, which serves anonymous visitors too — this button never did, because
                            it was gated on `defaultView === "wall"` and the server sets that only for a
                            logged-in viewer. Do not restore it: two controls for one destination is how
                            one of them ends up with the wrong gate. */}

                        <div className="w-full" data-playwright-id="user-section-lists"
                             hidden={effectiveView === "wall"}>
                        {section === "discussions" && (
                            <DiscussionsList
                                mode="authored"
                                ordering={normalizeUserDiscussionOrdering(ordering, "authored")}
                                username={username}
                                paginationApi={paginationApi}
                                discussionLanguageScope={discussionLanguageScope}
                                onClearLanguageScope={clearDiscussionLanguageScope}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                listQuery={listQuery}
                                onClearListQuery={clearListQuery}
                            />
                        )}
                        {section === "images" && (
                            <DiscussionsList
                                mode="images"
                                ordering={normalizeUserDiscussionOrdering(ordering, "authored")}
                                username={username}
                                paginationApi={paginationApi}
                                discussionLanguageScope={discussionLanguageScope}
                                onClearLanguageScope={clearDiscussionLanguageScope}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                listQuery={listQuery}
                                onClearListQuery={clearListQuery}
                            />
                        )}
                        {section === "participated" && (
                            <DiscussionsList
                                mode="participated"
                                ordering={normalizeUserDiscussionOrdering(ordering, "participated")}
                                username={username}
                                paginationApi={paginationApi}
                                discussionLanguageScope={discussionLanguageScope}
                                onClearLanguageScope={clearDiscussionLanguageScope}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                listQuery={listQuery}
                                onClearListQuery={clearListQuery}
                            />
                        )}
                        {section === "arguments" && (
                            <ArgumentsList
                                username={username}
                                paginationApi={paginationApi}
                                argumentOrdering={normalizeUserArgumentOrdering(ordering, "authored")}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                listQuery={listQuery}
                                onClearListQuery={clearListQuery}
                            />
                        )}
                        {section === "followers" && (
                            <FollowersList
                                username={username}
                                paginationApi={paginationApi}
                                refreshKey={followersRefreshKey}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                isSelfView={ownerView}
                            />
                        )}
                        {section === "following" && (
                            <FollowingList
                                username={username}
                                paginationApi={paginationApi}
                                refreshKey={followingRefreshKey}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                isSelfView={ownerView}
                            />
                        )}
                        {section === "groups" && (
                            <GroupsList
                                username={username}
                                paginationApi={paginationApi}
                                groupSection={groupSection}
                                groupOrdering={normalizeUserGroupOrdering(ordering)}
                                groupMembershipRefreshKey={groupMembershipRefreshKey}
                                discussionLanguageScope={discussionLanguageScope}
                                onClearLanguageScope={clearDiscussionLanguageScope}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                listQuery={listQuery}
                                onClearListQuery={clearListQuery}
                                isSelfView={ownerView}
                            />
                        )}
                        {section === "series" && (
                            <SeriesList
                                username={username}
                                paginationApi={paginationApi}
                                seriesOrdering={normalizeUserSeriesOrdering(ordering)}
                                discussionLanguageScope={discussionLanguageScope}
                                onClearLanguageScope={clearDiscussionLanguageScope}
                                localizer={localizer}
                                displayUsername={displayUsername}
                                sensitiveMasking={sensitiveMasking}
                                listQuery={listQuery}
                                onClearListQuery={clearListQuery}
                                isSelfView={ownerView}
                            />
                        )}
                        {shortSectionHandOn ? (
                            // `PROFILE-79`: one quiet hand-on under a short section (see the memo above).
                            // A real anchor, so it middle-clicks and opens in a new tab; a plain left click
                            // stays in the page and pushes the same entry a count chip would.
                            <div className="mt-4 flex justify-center" data-playwright-id="user-section-hand-on">
                                <a
                                    href={shortSectionHandOn.href}
                                    data-hand-on-section={shortSectionHandOn.section}
                                    className="relative touch-hit-44 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground standard-hover focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                    onClick={(event) => {
                                        if (!isPageInterceptableClick(event)) {
                                            return;
                                        }
                                        event.preventDefault();
                                        setRouteState(shortSectionHandOn.destination);
                                    }}
                                >
                                    {shortSectionHandOn.label}
                                    <ArrowRight className="size-4" aria-hidden="true" />
                                </a>
                            </div>
                        ) : null}
                        </div>
                    </div>
                )}
            />

            {/* `SV-134`. The avatar is a TERMINAL thumbnail: the member is already on the profile it
                points at, so it opens the picture instead of navigating to the page they are reading.
                The overlay is hosted HERE rather than inside `UserHeader` because the header is also
                mounted by `MyDashboardPage`, where the same avatar is a POINTER site and must keep
                navigating — the policy belongs to the host, never to the component.

                Rendered as a sibling of the layout, not inside the rail: it is `fixed`-positioned, and
                the rail carries `lg:overflow-y-auto` for `PROFILE-17`, which would clip it. */}
            {avatarLightbox}
        </SensitivePageGate>
    );
}

function parseOptionalCount(raw?: string | null): number | null {
    if (raw === undefined || raw === null || raw.trim() === "") {
        return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export const mountUserPage = createReactMount<Props>({
    name: "mountUserPage",
    getProps: ({ dataset, localizer }) => ({
        // `undefined`, not `"discussions"`, when the server did not name a section — that is the signal
        // the landing rule reads. An empty string is the same absence (JTE writes `data-section=""`).
        initialSection: dataset.section?.trim() || undefined,
        username: normalizeDisplayUsername(dataset.username) ?? undefined,
        initiallyFollowing: dataset.initiallyFollowing === "true",
        initialUserCreationDate: dataset.userCreationDate ?? null,
        initialUserDescription: dataset.userDescription ?? null,
        initialUserProfileStatement: dataset.userProfileStatement ?? null,
        initialUserProfilePictureUrl: dataset.userProfilePictureUrl ?? null,
        initialDiscussionsCount: parseOptionalCount(dataset.userDiscussionsCount),
        initialArgumentsCount: parseOptionalCount(dataset.userArgumentsCount),
        initialAgreementsReceivedCount: parseOptionalCount(dataset.userAgreementsCount),
        initialMindsChangedReceivedCount: parseOptionalCount(dataset.userMindsChangedCount),
        initialFollowersCount: parseOptionalCount(dataset.userFollowersCount),
        initialFollowingCount: parseOptionalCount(dataset.userFollowingCount),
        sensitive: dataset.sensitive === "true",
        hasInitialBootstrap: dataset.hasInitialBootstrap !== "false",
        // D16. Absent or empty means "not asked for" (null), so the deploy halves are order-independent:
        // a frontend that lands before the server writes this attribute behaves exactly as before.
        initialCanMessage: dataset.canMessage === "true" ? true : dataset.canMessage === "false" ? false : null,
        // P9. Absent means `sections`, which is the safe direction and makes the two deploy halves
        // order-independent: a frontend that lands before the server writes this attribute shows the
        // posts hub, never a wall a crawler could index.
        defaultMainColumnView: dataset.defaultView ?? null,
        localizer,
    }),
    render: (props) => <UserPage {...props} />,
});

export default UserPage;
